import { Link, useParams, useSearch } from '@tanstack/react-router';
import type { EnvSearch as Search } from '@/router';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EnvPageDialogs } from '@/features/env/EnvPageDialogs';
import { EnvPageHeader, ExpoBanner } from '@/features/env/EnvPageHeader';
import { EnvVarsTabs } from '@/features/env/EnvVarsTabs';
import { activeEnvironments } from '@/features/env/envPageModel';
import { useEnvDialogs } from '@/features/env/useEnvDialogs';
import { useEnvPage } from '@/features/env/useEnvPage';

/** Screen 06: scope selector + variables table, create/edit/version/reveal/import sheets, Resolve preview and the requirements editor. */
export function EnvVarsPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const search = useSearch({ strict: false }) as Search;
  const page = useEnvPage(slug, search);
  const dialogs = useEnvDialogs();
  const { projects, project } = page;

  if (projects.isLoading && !projects.data) return <PageSkeleton />;
  if (projects.isError && !projects.data) return <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />;
  if (!project) return <ProjectNotFound slug={slug} />;
  const environments = activeEnvironments(project);
  return (
    <div className="flex flex-col gap-5">
      <EnvPageHeader count={page.rows?.length} onImport={() => dialogs.setImporting(true)} onAdd={() => dialogs.setCreating(true)} />
      <ExpoBanner />
      <EnvVarsTabs page={page} dialogs={dialogs} environments={environments} tab={search.tab ?? 'variables'} />
      <EnvPageDialogs page={page} dialogs={dialogs} projectId={project.id} environments={environments} />
    </div>
  );
}

function ProjectNotFound({ slug }: { slug: string }) {
  return (
    <EmptyState
      icon="account_tree"
      title="Project not found"
      description={`There is no project with slug “${slug}” in this organization.`}
      action={
        <Button asChild variant="outline">
          <Link to="/">Back to Overview</Link>
        </Button>
      }
    />
  );
}

function PageSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      <Skeleton className="h-7 w-64" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-72 w-full rounded-lg" />
    </div>
  );
}
